import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  ArrowRightLeft, MapPin, Navigation, CheckCircle2, Clock, X,
  LogOut, RefreshCw, ArrowLeft, HardHat, Truck, Users,
  ChevronDown, ChevronUp, Loader2, Send, ArrowRight, BookOpen, Map
} from "lucide-react";
import LiveMap from "@/components/shared/LiveMap";
import TutorialLedger, { TutorialToggle } from "@/components/shared/TutorialLedger";
import DashboardSwitcher from "@/components/shared/DashboardSwitcher";
import TransportTimeline from "@/components/shared/TransportTimeline";

export default function TransportDashboard() {
  const { session, setSession, hubData, refreshData, logout } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const nav = useNavigate();
  const { sites, machines, requests } = hubData;

  const [toast, setToast] = useState(null);
  const [expandedReq, setExpandedReq] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedSite, setExpandedSite] = useState(null);
  const [showMap, setShowMap] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [viewMode, setViewMode] = useState("requests"); // "requests" | "timeline"

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  // Filter transport requests
  const transportRequests = useMemo(() =>
    requests.filter(r => r.request_type === "TRANSPORT")
      .sort((a, b) => {
        if (a.status === "DONE" && b.status !== "DONE") return 1;
        if (a.status !== "DONE" && b.status === "DONE") return -1;
        return new Date(b.created_at) - new Date(a.created_at);
      })
  , [requests]);

  const openTransports = transportRequests.filter(r => r.status !== "DONE");
  const doneTransports = transportRequests.filter(r => r.status === "DONE");

  // Machines in transit
  const inTransit = useMemo(() =>
    machines.filter(m => m.status_type === "TRANSPORT")
  , [machines]);

  const handleConfirmTransport = async (reqId) => {
    await api.confirmTransport(reqId);
    showToast("Transport confirmed! Machine relocated.");
    setExpandedReq(null);
    refreshData();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshData();
    setIsRefreshing(false);
  };

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col" data-testid="transport-dashboard">
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 left-4 right-4 z-[1000] bg-blue-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 font-bold text-sm uppercase tracking-wider animate-slideInDown max-w-lg mx-auto">
          <CheckCircle2 className="w-4 h-4" /> {toast}
        </div>
      )}

      {/* Header */}
      <header className="bg-white px-4 md:px-8 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-[0_0_15px_rgba(37,99,235,0.3)]">
            <ArrowRightLeft className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">Transport Portal</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{session?.hubName} / {session?.userName}</p>
          </div>
          <DashboardSwitcher currentRole="transport" />
        </div>
        <div className="flex items-center gap-2">
          <TutorialToggle onClick={() => setShowTutorial(!showTutorial)} />
          <button onClick={() => setShowMap(!showMap)} data-testid="toggle-map-btn"
            className={`p-2.5 rounded-xl transition-all border ${showMap ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-100 border-slate-200 text-slate-400 hover:text-slate-900"}`}>
            <Map className="w-4 h-4" />
          </button>
          <button onClick={handleRefresh} data-testid="transport-refresh"
            className={`p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all ${isRefreshing ? "animate-spin" : ""}`}>
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => { setSession(null); nav("/portal"); }} className="p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900" data-testid="back-to-lobby-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button onClick={() => { logout(); nav("/"); }} data-testid="transport-exit"
            className="p-2.5 bg-red-50 border border-red-200 rounded-xl text-red-500 hover:bg-red-600 hover:text-slate-900 transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <TutorialLedger role="transport" isOpen={showTutorial} onClose={() => setShowTutorial(false)} />

      <main className="flex-1 p-4 md:p-8 max-w-5xl mx-auto w-full">
        {/* Live Map */}
        {showMap && (
          <div className="mb-6" data-testid="map-section">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <Map className="w-3.5 h-3.5 text-blue-500" /> Live Map View
            </h2>
            <LiveMap sites={sites} machines={machines} requests={requests} />
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="p-4 bg-slate-50 border border-blue-500/20 rounded-xl text-center">
            <span className="text-2xl font-black text-blue-400 font-mono">{openTransports.length}</span>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Pending</p>
          </div>
          <div className="p-4 bg-slate-50 border border-amber-200 rounded-xl text-center">
            <span className="text-2xl font-black text-amber-400 font-mono">{inTransit.length}</span>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">In Transit</p>
          </div>
          <div className="p-4 bg-slate-50 border border-lime-500/20 rounded-xl text-center">
            <span className="text-2xl font-black text-lime-600 font-mono">{doneTransports.length}</span>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Completed</p>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center gap-1 mb-4 bg-slate-100 p-1 rounded-xl w-fit" data-testid="transport-view-toggle">
          <button onClick={() => setViewMode("requests")} data-testid="view-requests-btn"
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all flex items-center gap-1.5 ${viewMode === "requests" ? "bg-blue-600 text-white shadow-sm" : "text-slate-400 hover:text-slate-700"}`}>
            <Truck className="w-3.5 h-3.5" /> Requests
          </button>
          <button onClick={() => setViewMode("timeline")} data-testid="view-timeline-btn"
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all flex items-center gap-1.5 ${viewMode === "timeline" ? "bg-blue-600 text-white shadow-sm" : "text-slate-400 hover:text-slate-700"}`}>
            <Clock className="w-3.5 h-3.5" /> Timeline
          </button>
        </div>

        {viewMode === "timeline" ? (
          <TransportTimeline requests={requests} sites={sites} />
        ) : (
        <>
        {/* Active Transport Requests */}
        <div className="mb-6">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <Truck className="w-3.5 h-3.5 text-blue-500" /> Active Transport Requests ({openTransports.length})
          </h2>
          {openTransports.length === 0 ? (
            <div className="p-10 border border-dashed border-slate-200 rounded-xl text-center">
              <ArrowRightLeft className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-xs font-bold text-slate-400 uppercase">No pending transports</p>
            </div>
          ) : (
            <div className="space-y-3">
              {openTransports.map(req => {
                const fromSite = sites.find(s => s.id === req.site_id);
                const toSite = sites.find(s => s.id === req.target_site_id);
                const machine = machines.find(m => m.id === req.machine_id);
                const isExpanded = expandedReq === req.id;
                return (
                  <div key={req.id} className="bg-slate-50 border border-blue-500/30 rounded-xl overflow-hidden" data-testid={`transport-req-${req.id}`}>
                    <button onClick={() => setExpandedReq(isExpanded ? null : req.id)}
                      className="w-full p-4 flex items-center justify-between text-left">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white">
                          <Truck className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-900">{req.machine_number}</span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                              req.status === "IN_PROGRESS" ? "bg-amber-100 text-amber-600" : "bg-blue-500/10 text-blue-400"
                            }`}>{req.status === "IN_PROGRESS" ? "IN TRANSIT" : "PENDING"}</span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-[10px] text-slate-400 font-bold">{fromSite?.name || "Unknown"}</span>
                            <ArrowRight className="w-3 h-3 text-blue-400" />
                            <span className="text-[10px] text-blue-400 font-bold">{req.target_site_name || toSite?.name || "Unknown"}</span>
                          </div>
                          {req.assigned_driver && (
                            <div className="flex items-center gap-1 mt-1">
                              <Users className="w-3 h-3 text-lime-500" />
                              <span className="text-[10px] font-bold text-lime-600">{req.assigned_driver}</span>
                            </div>
                          )}
                          {!req.assigned_driver && req.status === "NEW" && (
                            <p className="text-[10px] text-amber-400 mt-1 font-bold">Awaiting driver assignment</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400 font-mono">{new Date(req.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="p-4 border-t border-slate-200 space-y-3 animate-fadeIn">
                        {/* Route visualization */}
                        <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200">
                          <div className="flex-1 text-center">
                            <MapPin className="w-5 h-5 text-red-500 mx-auto mb-1" />
                            <p className="text-xs font-bold text-slate-800">{fromSite?.name}</p>
                            <p className="text-[10px] text-slate-400">{fromSite?.city}</p>
                            {fromSite?.gps_lat > 0 && (
                              <a href={`https://www.google.com/maps?q=${fromSite.gps_lat},${fromSite.gps_lon}`} target="_blank" rel="noopener noreferrer"
                                className="text-[9px] text-lime-600 font-mono hover:underline flex items-center justify-center gap-0.5 mt-1">
                                <Navigation className="w-2.5 h-2.5" /> Maps
                              </a>
                            )}
                          </div>
                          <div className="px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-[2px] bg-blue-500" />
                              <Truck className="w-5 h-5 text-blue-400" />
                              <div className="w-8 h-[2px] bg-blue-500" />
                            </div>
                          </div>
                          <div className="flex-1 text-center">
                            <MapPin className="w-5 h-5 text-lime-500 mx-auto mb-1" />
                            <p className="text-xs font-bold text-slate-800">{req.target_site_name || toSite?.name}</p>
                            <p className="text-[10px] text-slate-400">{toSite?.city || ""}</p>
                            {toSite?.gps_lat > 0 && (
                              <a href={`https://www.google.com/maps?q=${toSite.gps_lat},${toSite.gps_lon}`} target="_blank" rel="noopener noreferrer"
                                className="text-[9px] text-lime-600 font-mono hover:underline flex items-center justify-center gap-0.5 mt-1">
                                <Navigation className="w-2.5 h-2.5" /> Maps
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Machine details */}
                        {machine && (
                          <div className="p-3 bg-white rounded-xl border border-slate-200">
                            <div className="flex items-center gap-2">
                              <HardHat className="w-4 h-4 text-amber-500" />
                              <span className="text-xs font-bold text-slate-800">{machine.name}</span>
                              {machine.brand && <span className="text-[10px] text-slate-400">{machine.brand}</span>}
                            </div>
                            {machine.gps_lat > 0 && (
                              <a href={`https://www.google.com/maps?q=${machine.gps_lat},${machine.gps_lon}`} target="_blank" rel="noopener noreferrer"
                                className="text-[10px] text-lime-600 font-mono flex items-center gap-1 mt-1.5 hover:underline">
                                <Navigation className="w-3 h-3" /> Machine GPS: {machine.gps_lat.toFixed(4)}, {machine.gps_lon.toFixed(4)}
                              </a>
                            )}
                          </div>
                        )}

                        {req.notes && <p className="text-xs text-slate-400 italic p-2 bg-white rounded-xl border border-slate-200">{req.notes}</p>}

                        <button data-testid={`confirm-transport-${req.id}`} onClick={() => handleConfirmTransport(req.id)}
                          className="w-full py-3.5 bg-blue-600 text-white rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 active:scale-95 transition-all shadow-[0_0_20px_rgba(37,99,235,0.2)]">
                          <CheckCircle2 className="w-5 h-5" /> Confirm Transport
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sites & Machine Map */}
        <div className="mb-6">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-lime-500" /> Sites & Machine Locations ({sites.length})
          </h2>
          <div className="space-y-2">
            {sites.map(site => {
              const sMachines = machines.filter(m => m.site_id === site.id);
              const transitMachines = sMachines.filter(m => m.status_type === "TRANSPORT");
              const isOpen = expandedSite === site.id;
              return (
                <div key={site.id} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden" data-testid={`transport-site-${site.id}`}>
                  <button onClick={() => setExpandedSite(isOpen ? null : site.id)}
                    className="w-full p-4 flex items-center justify-between text-left hover:bg-slate-100/30 transition-all">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <div>
                        <span className="text-sm font-bold text-slate-800">{site.name}</span>
                        <span className="text-[10px] text-slate-400 ml-2">{site.city}</span>
                      </div>
                      {site.gps_lat > 0 && <Navigation className="w-3 h-3 text-lime-500" />}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold bg-slate-100 text-slate-400 px-2 py-0.5 rounded">{sMachines.length}</span>
                      {transitMachines.length > 0 && (
                        <span className="text-[10px] font-bold bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded">{transitMachines.length} transit</span>
                      )}
                      {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="p-4 border-t border-slate-200 space-y-2 animate-fadeIn">
                      {site.gps_lat > 0 && (
                        <a href={`https://www.google.com/maps?q=${site.gps_lat},${site.gps_lon}`} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl mb-2 hover:border-lime-300">
                          <Navigation className="w-3.5 h-3.5 text-lime-500" />
                          <span className="text-[10px] font-mono text-lime-600">{site.gps_lat.toFixed(5)}, {site.gps_lon.toFixed(5)}</span>
                        </a>
                      )}
                      {sMachines.map(m => (
                        <div key={m.id} className={`flex items-center justify-between p-2.5 bg-white rounded-xl border ${
                          m.status_type === "TRANSPORT" ? "border-blue-500/30" : "border-slate-200"
                        }`}>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-800 font-mono">{m.machine_number}</span>
                            <span className="text-[10px] text-slate-400">{m.name}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {m.gps_lat > 0 && <Navigation className="w-3 h-3 text-lime-500" />}
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                              m.status_type === "TRANSPORT" ? "bg-blue-500/10 text-blue-400" :
                              m.status_type === "DEFECT" ? "bg-red-500/10 text-red-400" :
                              "bg-slate-100 text-slate-400"
                            }`}>{m.status_type || "OK"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Completed */}
        {doneTransports.length > 0 && (
          <div>
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-lime-500" /> Completed Transports ({doneTransports.length})
            </h2>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {doneTransports.slice(0, 20).map(req => (
                <div key={req.id} className="p-3 bg-slate-50/80 border border-slate-200 rounded-xl flex items-center justify-between opacity-60">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400">{req.machine_number}</span>
                    <span className="text-[9px] text-slate-400">{sites.find(s => s.id === req.site_id)?.name}</span>
                    <ArrowRight className="w-3 h-3 text-blue-400" />
                    <span className="text-[9px] text-blue-400">{req.target_site_name}</span>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-lime-500" />
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
